import { Controller } from "stimulus";

export default class extends Controller {
  static values = {
    url: String,
    date: String,
    view: String,
    weekLayout: String,
    monthLayout: String,
    locationId: String,
  };

  static targets = ["content"];

  connect() {
    this.loaded = false;
    this.query = "";
    this.boundDocumentClick = this.handleDocumentClick.bind(this);
    this.boundDocumentKeydown = this.handleDocumentKeydown.bind(this);
    document.addEventListener("click", this.boundDocumentClick);
    document.addEventListener("keydown", this.boundDocumentKeydown);
    this.handleSearch = (event) => {
      this.query = event.detail.query;
      this.applySearch();
    };
    document.addEventListener("boarding-search:changed", this.handleSearch);
    if (!this.viewValue) this.viewValue = "week";
    if (!this.weekLayoutValue) this.weekLayoutValue = "cards";
    if (!this.monthLayoutValue) this.monthLayoutValue = "cards";
    this.tabLink = document.querySelector('a[href="#daycarecalendar"]');
    this.handleTabShown = () => this.loadOnce();
    if (this.tabLink) $(this.tabLink).on("shown.bs.tab", this.handleTabShown);
    if (this.element.classList.contains("active")) this.loadOnce();
  }

  disconnect() {
    document.removeEventListener("click", this.boundDocumentClick);
    document.removeEventListener("keydown", this.boundDocumentKeydown);
    document.removeEventListener("boarding-search:changed", this.handleSearch);
    if (this.tabLink) $(this.tabLink).off("shown.bs.tab", this.handleTabShown);
  }

  loadOnce() {
    if (this.loaded) return;
    this.loaded = true;
    this.load();
  }

  localDateString(d) {
    return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
  }

  parseDate(str) {
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  load() {
    const params = new URLSearchParams({
      date: this.dateValue,
      view: this.viewValue,
      week_layout: this.weekLayoutValue,
      month_layout: this.monthLayoutValue,
      location_id: this.locationIdValue,
    });
    this.contentTarget.style.opacity = 0.5;
    fetch(`${this.urlValue}?${params.toString()}`, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then((response) => response.text())
      .then((html) => {
        this.popover = null;
        this.popoverBar = null;
        this.contentTarget.innerHTML = html;
        this.contentTarget.style.opacity = 1;
        this.applySearch();
        this.scrollToToday();
      });
  }

  scrollToToday() {
    const scroller = this.contentTarget.querySelector(".dc-scroll");
    if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;
    const today = scroller.querySelector(".dc-today, .dc-today-cell");
    const first = scroller.querySelector(".dc-day, .dc-month-cell");
    if (!today || !first) return;
    scroller.scrollLeft = today.getBoundingClientRect().left - first.getBoundingClientRect().left;
  }

  applySearch() {
    const query = this.query.trim();
    this.contentTarget.querySelectorAll(".js-daycare-searchable").forEach((el) => {
      el.classList.toggle("d-none", query !== "" && !el.dataset.search.includes(query));
    });
    this.contentTarget.querySelectorAll(".dc-service-card").forEach((card) => {
      const rows = card.querySelectorAll(".js-daycare-searchable");
      const anyVisible = Array.from(rows).some((row) => !row.classList.contains("d-none"));
      card.classList.toggle("d-none", query !== "" && rows.length > 0 && !anyVisible);
    });
  }

  previous() {
    this.shift(-1);
  }

  next() {
    this.shift(1);
  }

  shift(direction) {
    const d = this.parseDate(this.dateValue);
    if (this.viewValue === "week") {
      d.setDate(d.getDate() + 7 * direction);
    } else {
      d.setDate(1);
      d.setMonth(d.getMonth() + direction);
    }
    this.dateValue = this.localDateString(d);
    this.load();
  }

  today() {
    this.dateValue = this.localDateString(new Date());
    this.load();
  }

  pickDate(event) {
    const controller = this;
    const label = event.currentTarget;
    if (label._flatpickr) {
      label._flatpickr.open();
      return;
    }
    const instance = flatpickr(label, {
      disableMobile: "true",
      defaultDate: this.dateValue,
      onChange: function (selectedDates) {
        if (selectedDates.length === 1) {
          controller.dateValue = controller.localDateString(selectedDates[0]);
          controller.load();
        }
      },
    });
    instance.open();
  }

  toggleBlocks(event) {
    const button = event.currentTarget;
    const column = button.closest(".dc-lane-day, .dc-card-blocks");
    const expanded = column.classList.toggle("expanded");
    button.textContent = expanded ? button.dataset.lessLabel : button.dataset.moreLabel;
  }

  togglePanelBlocks(event) {
    const button = event.currentTarget;
    const list = button.closest(".dc-m-panel-blocks");
    const expanded = list.classList.toggle("expanded");
    button.setAttribute("aria-expanded", expanded ? "true" : "false");
    button.querySelector(".dc-m-panel-more-label").textContent = expanded ? "Show less" : button.dataset.moreLabel;
    button.querySelector(".dc-m-panel-more-icon-down").classList.toggle("d-none", expanded);
    button.querySelector(".dc-m-panel-more-icon-up").classList.toggle("d-none", !expanded);
  }

  resetPanelBlocks(panel) {
    const list = panel.querySelector(".dc-m-panel-blocks");
    if (!list || !list.classList.contains("expanded")) return;
    list.querySelector(".dc-m-panel-more").click();
  }

  toggleLaneAll(event) {
    const button = event.currentTarget;
    const lane = button.closest(".dc-lane");
    const expanded = lane.classList.toggle("dc-lane-expanded");
    button.textContent = expanded ? button.dataset.lessLabel : button.dataset.moreLabel;
  }

  toggleBlockPopover(event) {
    event.stopPropagation();
    const bar = event.currentTarget;
    if (this.popoverBar === bar) {
      this.closeBlockPopover();
      return;
    }
    const popover = this.contentTarget.querySelector(`.dc-m-popover[data-block-id="${bar.dataset.blockId}"]`);
    if (!popover) return;
    this.closeBlockPopover(false);
    this.popoverBar = bar;
    this.popover = popover;
    bar.classList.add("dc-m-block-selected");
    bar.setAttribute("aria-expanded", "true");
    popover.classList.add("open");
    this.positionPopover(popover, bar);
    const close = popover.querySelector(".dc-m-pop-close");
    if (close) close.focus({ preventScroll: true });
  }

  positionPopover(popover, bar) {
    const container = this.contentTarget;
    if (popover.parentElement !== container) container.appendChild(popover);
    const column = Number(bar.dataset.col);
    const monthWeek = bar.closest(".dc-m-week");
    const cell = monthWeek
      ? monthWeek.querySelectorAll(".dc-m-cell")[column]
      : bar.closest(".dc-week-wrap").querySelectorAll(".dc-day")[column];
    const containerRect = container.getBoundingClientRect();
    const barRect = bar.getBoundingClientRect();
    const cellRect = cell.getBoundingClientRect();
    const width = popover.offsetWidth;
    const height = popover.offsetHeight;
    let top = barRect.bottom - containerRect.top + 4;
    if (barRect.bottom + height + 8 > window.innerHeight && barRect.top - height - 8 > 0) {
      top = barRect.top - containerRect.top - height - 4;
    }
    const left = column <= 3 ? cellRect.left - containerRect.left + 6 : cellRect.right - containerRect.left - 6 - width;
    popover.style.top = `${top}px`;
    popover.style.left = `${Math.min(Math.max(0, left), Math.max(0, containerRect.width - width))}px`;
  }

  closeBlockPopover(restoreFocus = true) {
    if (!this.popover) return;
    const bar = this.popoverBar;
    this.popover.classList.remove("open");
    bar.classList.remove("dc-m-block-selected");
    bar.setAttribute("aria-expanded", "false");
    this.popover = null;
    this.popoverBar = null;
    if (restoreFocus && bar.isConnected) bar.focus({ preventScroll: true });
  }

  handleDocumentClick(event) {
    if (!this.popover) return;
    if (event.target.closest(".dc-m-popover") || event.target.closest(".dc-m-block") || event.target.closest(".dc-lane-bar") || event.target.closest(".dc-m-panel-block")) return;
    this.closeBlockPopover(false);
  }

  handleDocumentKeydown(event) {
    if (event.key === "Escape" && this.popover) this.closeBlockPopover();
  }

  toggleWeekBlocks(event) {
    event.stopPropagation();
    const week = event.currentTarget.closest(".dc-m-week");
    if (this.popoverBar && week.contains(this.popoverBar)) this.closeBlockPopover(false);
    const expanded = week.classList.toggle("dc-m-expanded");
    week.querySelectorAll(".dc-m-more").forEach((link) => link.setAttribute("aria-expanded", expanded ? "true" : "false"));
  }

  keyToggleDay(event) {
    if (event.key !== "Enter" && event.key !== " ") return;
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.toggleDay(event);
  }

  toggleDay(event) {
    if (event.target.closest && event.target.closest("a")) return;
    if (this.dayAnimating) return;
    const cell = event.currentTarget;
    const panel = this.contentTarget.querySelector(`.dc-m-panel[data-date="${cell.dataset.date}"]`);
    if (!panel) return;
    const current = this.contentTarget.querySelector(".dc-m-panel.open");
    if (current === panel) {
      this.collapsePanel(current);
    } else if (current) {
      this.collapsePanel(current, () => this.expandPanel(panel));
    } else {
      this.expandPanel(panel);
    }
  }

  closeDay(event) {
    event.stopPropagation();
    if (this.dayAnimating) return;
    const panel = event.currentTarget.closest(".dc-m-panel");
    if (panel) this.collapsePanel(panel);
  }

  expandPanel(panel) {
    panel.classList.add("open");
    this.syncSelectedDay(panel.dataset.date);
    this.contentTarget.querySelector(".dc-month").classList.add("has-selection");
  }

  collapsePanel(panel, done) {
    this.dayAnimating = true;
    panel.classList.add("closing");
    setTimeout(() => {
      this.resetPanelBlocks(panel);
      panel.classList.remove("open", "closing");
      this.dayAnimating = false;
      this.syncSelectedDay(null);
      if (done) {
        done();
      } else {
        this.contentTarget.querySelector(".dc-month").classList.remove("has-selection");
      }
    }, 180);
  }

  syncSelectedDay(date) {
    this.contentTarget.querySelectorAll(".dc-m-cell").forEach((cell) => {
      const selected = date !== null && cell.dataset.date === date;
      cell.classList.toggle("dc-m-selected", selected);
      if (cell.hasAttribute("aria-expanded")) cell.setAttribute("aria-expanded", selected ? "true" : "false");
    });
  }

  setView(event) {
    this.viewValue = event.currentTarget.dataset.view;
    this.load();
  }

  setLayout(event) {
    const layout = event.currentTarget.dataset.layout;
    if (this.viewValue === "week") {
      this.weekLayoutValue = layout;
    } else {
      this.monthLayoutValue = layout;
    }
    this.load();
  }

}
